import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import type { WorkflowItem } from "@/api/models"

import { Clock, Plus, Search, Star, X } from "@/components/icons"
import { BuilderTabs } from "@/components/layouts/builder-tabs"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { SettingsService } from "@/services/settings-service"

import { WorkflowTile } from "./workflow-tile"
import { canCreateFlows } from "../utils/can-create-flows"
import { isWorkflowAdminFromSettings } from "../utils/workflow-admin-policy"
import {
  canEditWorkflow,
  canFavoriteWorkflow,
  canScheduleWorkflow,
} from "../utils/workflow-permissions"

type FlowTabsProps = {
  workflows: WorkflowItem[]
  onOpen: (workflow: WorkflowItem) => void
  onDuplicate: (workflow: WorkflowItem) => void
  onToggleFavorite: (workflow: WorkflowItem) => void
  onDelete: (workflow: WorkflowItem) => void
}

function sortByRecent(workflows: WorkflowItem[]): WorkflowItem[] {
  return [...workflows].sort((workflowA, workflowB) => {
    if (workflowA.last_run && workflowB.last_run) {
      return workflowB.last_run.localeCompare(workflowA.last_run)
    }
    if (workflowA.last_run && !workflowB.last_run) return -1
    if (!workflowA.last_run && workflowB.last_run) return 1

    const dateA = workflowA.updated_at ? new Date(workflowA.updated_at).getTime() : 0
    const dateB = workflowB.updated_at ? new Date(workflowB.updated_at).getTime() : 0
    return dateB - dateA
  })
}

export function FlowTabs({
  workflows,
  onOpen,
  onDuplicate,
  onToggleFavorite,
  onDelete,
}: FlowTabsProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "workflowLanding" })
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState("")
  const [activeTab, setActiveTab] = useState("recent")
  const tabsContentClassName = "min-h-[calc(3*16rem+2*1rem)]"

  const settingsService = SettingsService.getInstance()
  const isWorkflowAdmin = isWorkflowAdminFromSettings(settingsService.settings)

  const filteredWorkflows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return workflows

    return workflows.filter((workflow) => {
      return (
        workflow.title.toLowerCase().includes(query) ||
        (workflow.description?.toLowerCase().includes(query) ?? false) ||
        (workflow.category?.toLowerCase().includes(query) ?? false) ||
        (workflow.tags?.some((tag) => tag.toLowerCase().includes(query)) ?? false)
      )
    })
  }, [workflows, searchQuery])

  const recentWorkflows = useMemo(() => {
    const withActivity = filteredWorkflows.filter(
      (workflow) => workflow.last_run || workflow.updated_at
    )
    return sortByRecent(withActivity).slice(0, 12)
  }, [filteredWorkflows])

  const myWorkflows = useMemo(() => {
    return sortByRecent(filteredWorkflows.filter((workflow) => workflow.visibility === "personal"))
  }, [filteredWorkflows])

  const companyWorkflows = useMemo(() => {
    return sortByRecent(filteredWorkflows.filter((workflow) => workflow.visibility === "company"))
  }, [filteredWorkflows])

  const favoriteWorkflows = useMemo(() => {
    return sortByRecent(
      filteredWorkflows.filter(
        (workflow) => workflow.is_favorite && canFavoriteWorkflow(workflow, isWorkflowAdmin)
      )
    )
  }, [filteredWorkflows, isWorkflowAdmin])

  const sharedWorkflows = useMemo(() => {
    return sortByRecent(filteredWorkflows.filter((workflow) => workflow.visibility === "shared"))
  }, [filteredWorkflows])

  const hasSharedWorkflows = workflows.some((workflow) => workflow.visibility === "shared")

  const renderWorkflowGrid = (
    workflowList: WorkflowItem[],
    emptyKey: string,
    emptyIcon: "clock" | "star" = "clock"
  ) => {
    if (workflowList.length === 0) {
      const IconComponent = emptyIcon === "star" ? Star : Clock
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <IconComponent className="text-muted-foreground/40 mb-3 size-10" />
          <p className="text-muted-foreground text-sm">
            {searchQuery ? t("noMatches") : t(emptyKey)}
          </p>
          {!searchQuery && canCreateFlows() && (
            <Button className="mt-4" size="sm" onClick={() => void navigate("/flows/new")}>
              <Plus className="mr-2 size-4" />
              {t("emptyCta")}
            </Button>
          )}
        </div>
      )
    }

    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {workflowList.map((workflow) => (
          <WorkflowTile
            key={workflow.id}
            canEdit={canEditWorkflow(workflow, isWorkflowAdmin)}
            canFavorite={canFavoriteWorkflow(workflow, isWorkflowAdmin)}
            canSchedule={canScheduleWorkflow(workflow, isWorkflowAdmin)}
            workflow={workflow}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
            onOpen={onOpen}
            onToggleFavorite={onToggleFavorite}
          />
        ))}
      </div>
    )
  }

  const searchSlot = (
    <div className="relative w-full sm:max-w-xs">
      <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
      <Input
        className="h-9 pr-8 pl-9"
        placeholder={t("searchPlaceholder")}
        value={searchQuery}
        onChange={(event) => setSearchQuery(event.target.value)}
      />
      {searchQuery && (
        <Button
          aria-label={t("clearSearch")}
          className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2.5 size-7 -translate-y-1/2 p-0"
          size="icon"
          type="button"
          variant="ghost"
          onClick={() => setSearchQuery("")}
        >
          <X className="size-4" />
        </Button>
      )}
    </div>
  )

  return (
    <div className="w-full">
      <BuilderTabs
        contentClassName={tabsContentClassName}
        searchSlot={searchSlot}
        value={activeTab}
        tabs={[
          {
            value: "recent",
            label: t("tabs.recent"),
            content: renderWorkflowGrid(recentWorkflows, "tabs.recentEmpty"),
          },
          {
            value: "favorites",
            label: t("tabs.favorites"),
            content: renderWorkflowGrid(favoriteWorkflows, "tabs.favoritesEmpty", "star"),
          },
          {
            value: "my-flows",
            label: t("tabs.myFlows"),
            content: renderWorkflowGrid(myWorkflows, "tabs.myFlowsEmpty"),
          },
          {
            value: "company",
            label: t("tabs.company"),
            content: renderWorkflowGrid(companyWorkflows, "tabs.companyEmpty"),
          },
          ...(hasSharedWorkflows
            ? [
                {
                  value: "shared",
                  label: t("tabs.shared"),
                  content: renderWorkflowGrid(sharedWorkflows, "tabs.sharedEmpty"),
                },
              ]
            : []),
        ]}
        onValueChange={setActiveTab}
      />
    </div>
  )
}
